import { useState, useRef, useCallback } from "react";
import { useNavigate } from "@/lib/router-compat";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Paperclip, ArrowUp, FileText, Type, Link as LinkIcon, X, File, Loader2, Sparkles, ChevronDown } from "lucide-react";
import { YoutubeIcon } from "@/components/icons/CustomIcons";
import { trackSignupIntent } from "@/lib/analytics";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

type AttachmentType = "file" | "text" | "url" | "youtube";
type InputMode = "default" | "text" | "url" | "youtube";

interface Attachment {
  type: AttachmentType;
  file?: File;
  label: string;
}

interface LandingInputBarProps {
  onSubmit: (query: string, attachments: Attachment[]) => void;
  isLoading?: boolean;
}

const MAX_FILES = 15;
const ACCEPTED = ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.md,.csv,.json,.xml,.yaml,.yml,.html,.htm,.css,.js,.jsx,.ts,.tsx,.py,.java,.c,.cpp,.h,.hpp,.go,.rs,.php,.rb,.swift,.kt,.sql,.log,.rtf,.odt,.ods,.odp";

export function LandingInputBar({ onSubmit, isLoading = false }: LandingInputBarProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const prefersReducedMotion = useReducedMotion();
  const [query, setQuery] = useState("");
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [isFocused, setIsFocused] = useState(false);
  const [inputMode, setInputMode] = useState<InputMode>("default");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleFiles = useCallback((files: FileList | null) => {
    if (!files) return;
    const newAttachments: Attachment[] = Array.from(files)
      .slice(0, MAX_FILES - attachments.filter(a => a.type === "file").length)
      .map(f => ({ type: "file" as const, file: f, label: f.name }));
    setAttachments(prev => [...prev, ...newAttachments]);
  }, [attachments]);

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    if (!query.trim() && attachments.length === 0) return;
    onSubmit(query, attachments);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const adjustHeight = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 120) + "px";
  };

  const selectMode = (mode: InputMode) => {
    setInputMode(mode);
    setQuery("");
    setTimeout(() => textareaRef.current?.focus(), 50);
  };

  const clearMode = () => {
    setInputMode("default");
    setQuery("");
  };

  const hasContent = query.trim().length > 0 || attachments.length > 0;

  const modeConfig: Record<Exclude<InputMode, "default">, { icon: React.ReactNode; label: string; placeholder: string }> = {
    text: {
      icon: <Type className="w-3.5 h-3.5 text-primary" />,
      label: t("landing.inputBar.pasteText", "Paste text"),
      placeholder: t("landing.inputBar.pasteTextPlaceholder", "Paste your course notes, lecture transcript, or any study material here..."),
    },
    url: {
      icon: <LinkIcon className="w-3.5 h-3.5 text-primary" />,
      label: t("landing.inputBar.pasteUrl", "Paste a URL"),
      placeholder: t("landing.inputBar.pasteUrlPlaceholder", "Paste the URL of the page you want to study from..."),
    },
    youtube: {
      icon: <YoutubeIcon className="w-3.5 h-3.5 text-red-500" />,
      label: t("landing.inputBar.youtubeLink", "YouTube link"),
      placeholder: t("landing.inputBar.youtubePlaceholder", "Paste the YouTube video URL here (e.g. https://youtube.com/watch?v=...)"),
    },
  };

  const getPlaceholder = () => {
    if (inputMode !== "default") return modeConfig[inputMode].placeholder;
    return t("landing.inputBar.placeholder", "Upload your course or describe what you want to study...");
  };

  return (
    <div className="w-full max-w-2xl mx-auto">
      <motion.div
        initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, type: "spring", stiffness: 80, damping: 20 }}
        className={`atlas-prompt relative rounded-2xl border bg-card/72 backdrop-blur-2xl transition-all duration-200 ${
          isFocused ? "border-primary/40 ring-4 ring-primary/10" : "border-primary/15"
        }`}
      >
        {/* Active mode label */}
        <AnimatePresence>
          {inputMode !== "default" && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="flex items-center gap-2 px-4 pt-3">
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-primary/10 border border-primary/20 text-xs font-medium text-primary">
                  {modeConfig[inputMode].icon}
                  <span>{modeConfig[inputMode].label}</span>
                  <button
                    onClick={clearMode}
                    className="ml-1 hover:text-destructive transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Attachments preview */}
        <AnimatePresence>
          {attachments.length > 0 && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="flex flex-wrap gap-2 px-4 pt-3">
                {attachments.map((att, i) => (
                  <motion.div
                    key={i}
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.8, opacity: 0 }}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-secondary/80 border border-border/40 text-xs text-foreground/80 max-w-[200px]"
                  >
                    <File className="w-3 h-3 shrink-0 text-primary" />
                    <span className="truncate">{att.label}</span>
                    <button
                      onClick={() => removeAttachment(i)}
                      className="shrink-0 hover:text-destructive transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Input row */}
        <div className="flex items-end gap-2 px-3 py-5">
          {/* Attach button */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="shrink-0 w-9 h-9 flex items-center justify-center rounded-xl hover:bg-primary/10 transition-colors text-muted-foreground hover:text-foreground"
                aria-label={t("landing.inputBar.attach", "Attach")}
              >
                <Paperclip className="w-5 h-5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              <DropdownMenuItem onClick={() => fileInputRef.current?.click()} className="gap-3">
                <FileText className="w-4 h-4 text-primary" />
                {t("landing.inputBar.uploadFile", "Upload a file")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => selectMode("text")} className="gap-3">
                <Type className="w-4 h-4 text-primary" />
                {t("landing.inputBar.pasteText", "Paste text")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => selectMode("url")} className="gap-3">
                <LinkIcon className="w-4 h-4 text-primary" />
                {t("landing.inputBar.pasteUrl", "Paste a URL")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => selectMode("youtube")} className="gap-3">
                <YoutubeIcon className="w-4 h-4 text-red-500" />
                {t("landing.inputBar.youtubeLink", "YouTube link")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Textarea */}
          <textarea
            ref={textareaRef}
            value={query}
            onChange={(e) => { setQuery(e.target.value); adjustHeight(); }}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            placeholder={getPlaceholder()}
            rows={inputMode === "text" ? 3 : 1}
            className={`flex-1 min-w-0 bg-transparent border-none outline-hidden resize-none text-base placeholder:text-muted-foreground py-1.5 max-h-[120px] leading-relaxed ${
              inputMode === "text" ? "min-h-[80px]" : "min-h-[36px]"
            }`}
          />

          {/* Model badge */}
          <div className="hidden sm:flex shrink-0 h-9 items-center gap-1 px-3 rounded-md bg-secondary/60 text-[11px] font-medium text-muted-foreground select-none">
            <Sparkles className="w-3 h-3 text-primary/60" />
            <span>Atlas-4</span>
          </div>

          {/* Send button */}
          <motion.button
            onClick={handleSubmit}
            disabled={!hasContent || isLoading}
            whileHover={hasContent && !isLoading ? { scale: 1.05 } : {}}
            whileTap={hasContent && !isLoading ? { scale: 0.95 } : {}}
            className={`shrink-0 w-9 h-9 flex items-center justify-center rounded-xl transition-all duration-200 ${
              isLoading
                ? "bg-primary text-primary-foreground shadow-sm"
                : hasContent
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "bg-secondary/60 text-muted-foreground cursor-not-allowed"
            }`}
            aria-label={t("landing.inputBar.send", "Send")}
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <ArrowUp className="w-5 h-5" />
            )}
          </motion.button>
        </div>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ACCEPTED}
          onChange={(e) => handleFiles(e.target.files)}
          className="hidden"
        />
      </motion.div>
    </div>
  );
}
